import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  KeyRound,
  Pencil,
  Plus,
  Power,
  PowerOff,
  ShieldCheck,
  UserCog,
  Users,
} from 'lucide-react';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import InitialCredentialsModal from '../components/common/InitialCredentialsModal';
import Modal from '../components/common/Modal';
import Input from '../components/forms/Input';
import SearchInput from '../components/forms/SearchInput';
import Select from '../components/forms/Select';
import Pagination from '../components/tables/Pagination';
import Table from '../components/tables/Table';
import {
  ACTIONS,
  RESOURCES,
  ROLE_LABELS,
  ROLES,
} from '../config/accessControl';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { userService } from '../services';

const creatableRole = {
  [ROLES.ADMINISTRADOR]: ROLES.ADMINISTRADOR_GENERAL,
  [ROLES.ADMINISTRADOR_GENERAL]: ROLES.COORDINADOR,
  [ROLES.COORDINADOR]: ROLES.PUNTERO,
};

const roleOptions = Object.entries(ROLE_LABELS)
  .filter(([role]) => role !== ROLES.VOTANTE)
  .map(([value, label]) => ({ value, label }));

const adminCreatableRoleOptions = [
  ROLES.ADMINISTRADOR_GENERAL,
  ROLES.COORDINADOR,
  ROLES.PUNTERO,
].map((value) => ({ value, label: ROLE_LABELS[value] }));

function UsersPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [modalMode, setModalMode] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const selectedRole = watch('role');

  const canCreate = can(RESOURCES.USERS, ACTIONS.CREATE);
  const canConfigure = can(RESOURCES.USERS, ACTIONS.CONFIGURE);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await userService.getForActor(user, {
        query,
        role: roleFilter,
        status: statusFilter,
        page,
        pageSize: 8,
      });
      setUsers(response.data);
      setMeta(response.meta);
      if (response.meta.page !== page) setPage(response.meta.page);
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [page, query, roleFilter, statusFilter, user]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!modalMode || user.role !== ROLES.ADMINISTRADOR) return;
    if (![ROLES.COORDINADOR, ROLES.PUNTERO].includes(selectedRole)) {
      setSupervisorOptions([]);
      setValue('superiorId', '');
      return;
    }
    let mounted = true;
    userService.getSupervisorOptions(selectedRole)
      .then((response) => mounted && setSupervisorOptions(
        response.data
          .filter((option) => option.userId !== editingUser?.id)
          .map((option) => ({ value: option.id, label: option.name }))
      ))
      .catch(() => mounted && setSupervisorOptions([]));
    return () => { mounted = false; };
  }, [editingUser?.id, modalMode, selectedRole, setValue, user]);

  useEffect(() => {
    setPage(1);
  }, [query, roleFilter, statusFilter]);

  const counts = useMemo(() => ({
    active: users.filter((item) => item.status === 'active').length,
    inactive: users.filter((item) => item.status !== 'active').length,
  }), [users]);

  const openCreate = () => {
    const role = creatableRole[user.role];
    setEditingUser(null);
    setFormError('');
    setSupervisorOptions([]);
    reset({
      nombres: '',
      apellidos: '',
      cedula: '',
      celular: '',
      seccional: '',
      email: '',
      status: 'active',
      role,
    });
    setModalMode('create');
  };

  const openEdit = (target) => {
    setEditingUser(target);
    setFormError('');
    reset({
      nombres: target.nombres || '',
      apellidos: target.apellidos || '',
      cedula: target.cedula || target.username,
      celular: target.celular || '',
      seccional: target.seccional || '',
      email: target.email,
      status: target.status,
      role: target.role,
      superiorId: target.superiorId || '',
    });
    setModalMode('edit');
  };

  const closeModal = () => {
    if (saving) return;
    setModalMode(null);
    setEditingUser(null);
    setFormError('');
  };

  const submitUser = async (values) => {
    setSaving(true);
    setFormError('');
    try {
      if (modalMode === 'create') {
        const response = await userService.createForActor(values, user);
        setCredentials({
          username: response.data.username,
          initialPassword: response.meta.initialPassword,
          mustChangePassword: response.meta.mustChangePassword,
        });
      } else {
        await userService.updateForActor(editingUser.id, values, user);
        setFeedback({ title: 'Usuario actualizado', message: 'Los cambios se guardaron correctamente.' });
      }
      setModalMode(null);
      setEditingUser(null);
      await loadUsers();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const executeStatusChange = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.type === 'activate') {
        await userService.activate(confirmAction.user.id, user);
      } else {
        await userService.deactivate(confirmAction.user.id, user);
      }
      setFeedback({
        title: confirmAction.type === 'activate' ? 'Usuario activado' : 'Usuario desactivado',
        message: `${confirmAction.user.name} fue actualizado correctamente.`,
      });
      setConfirmAction(null);
      await loadUsers();
    } catch (error) {
      setConfirmAction(null);
      setFeedback({ title: 'No se pudo completar', message: error.message, variant: 'danger' });
    }
  };

  const resetPassword = async (target) => {
    try {
      const response = await userService.resetPassword(target.id, user);
      setFeedback({
        title: 'Contraseña restablecida',
        message: response.meta.mustChangePassword
          ? `${target.name} deberá cambiar su contraseña al volver a ingresar.`
          : `${target.name} ya puede ingresar con la contraseña restablecida.`,
      });
      setCredentials({
        username: target.username,
        initialPassword: response.meta.initialPassword,
        mustChangePassword: response.meta.mustChangePassword,
      });
    } catch (error) {
      setFeedback({ title: 'No se pudo restablecer', message: error.message, variant: 'danger' });
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Usuario',
      render: (value, row) => (
        <div>
          <p className="font-semibold text-content-primary">{value}</p>
          <p className="mt-0.5 text-xs text-content-muted">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Rol',
      render: (value) => <Badge variant="primary">{ROLE_LABELS[value]}</Badge>,
    },
    {
      key: 'superiorName',
      header: 'Responsable superior',
      render: (value) => value || <span className="text-content-muted">Sin responsable</span>,
    },
    { key: 'celular', header: 'Celular' },
    {
      key: 'seccional',
      header: 'Seccional',
      render: (value) => value || <span className="text-content-muted">—</span>,
    },
    {
      key: 'status',
      header: 'Estado',
      render: (value) => (
        <Badge variant={value === 'active' ? 'success' : 'neutral'} dot>
          {value === 'active' ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label={`Editar ${row.name}`}>
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmAction({ type: row.status === 'active' ? 'deactivate' : 'activate', user: row })}
            aria-label={`${row.status === 'active' ? 'Desactivar' : 'Activar'} ${row.name}`}
          >
            {row.status === 'active' ? <PowerOff className="size-4 text-danger-600" /> : <Power className="size-4 text-success-600" />}
          </Button>
          {canConfigure && (
            <Button variant="ghost" size="sm" onClick={() => resetPassword(row)} aria-label={`Restablecer contraseña de ${row.name}`}>
              <KeyRound className="size-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const modalTitle = modalMode === 'create'
    ? user.role === ROLES.ADMINISTRADOR
      ? `Crear ${ROLE_LABELS[selectedRole] || 'usuario'}`
      : `Crear ${ROLE_LABELS[creatableRole[user.role]] || 'usuario'}`
    : 'Editar usuario';

  return (
    <div>
      <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <p className="text-sm font-semibold text-primary-700">Administración de acceso</p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-content-primary">Usuarios</h2>
          <p className="mt-1 text-sm text-content-muted">Gestiona las cuentas disponibles dentro de tu estructura autorizada.</p>
        </div>
        {canCreate && creatableRole[user.role] && (
          <Button onClick={openCreate}><Plus className="size-4" /> Crear usuario</Button>
        )}
      </div>

      {feedback && (
        <Alert variant={feedback.variant || 'success'} title={feedback.title} className="mt-5" onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}
      {loadError && <Alert variant="danger" title="No se pudieron cargar los usuarios" className="mt-5">{loadError}</Alert>}

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Card><div className="flex items-center justify-between"><div><p className="text-sm text-content-muted">Usuarios visibles</p><p className="mt-1 text-2xl font-semibold">{meta.total}</p></div><span className="grid size-10 place-items-center rounded-control bg-primary-50 text-primary-700"><Users className="size-5" /></span></div></Card>
        <Card><div className="flex items-center justify-between"><div><p className="text-sm text-content-muted">Activos en esta página</p><p className="mt-1 text-2xl font-semibold">{counts.active}</p></div><span className="grid size-10 place-items-center rounded-control bg-success-50 text-success-700"><ShieldCheck className="size-5" /></span></div></Card>
        <Card><div className="flex items-center justify-between"><div><p className="text-sm text-content-muted">Inactivos en esta página</p><p className="mt-1 text-2xl font-semibold">{counts.inactive}</p></div><span className="grid size-10 place-items-center rounded-control bg-slate-100 text-content-muted"><UserCog className="size-5" /></span></div></Card>
      </div>

      <Card className="mt-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="w-full lg:max-w-md">
            <SearchInput value={query} onChange={(event) => setQuery(event.target.value)} onClear={() => setQuery('')} placeholder="Buscar nombre, usuario o correo..." aria-label="Buscar usuarios" />
          </div>
          <Select id="user-role-filter" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} placeholder="Todos los roles" options={roleOptions} className="w-full lg:w-56" />
          <Select id="user-status-filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} placeholder="Todos los estados" options={[{ value: 'active', label: 'Activos' }, { value: 'inactive', label: 'Inactivos' }]} className="w-full lg:w-48" />
        </div>
      </Card>

      <div className="mt-5">
        {loading ? (
          <Card><div className="grid min-h-52 place-items-center"><LoadingSpinner label="Cargando usuarios" /></div></Card>
        ) : (
          <>
            <Table columns={columns} data={users} emptyMessage="No hay usuarios que coincidan con los filtros." />
            {meta.totalPages > 1 && <div className="mt-4"><Pagination page={meta.page} totalPages={meta.totalPages} onPageChange={setPage} /></div>}
          </>
        )}
      </div>

      <Modal
        open={Boolean(modalMode)}
        onClose={closeModal}
        title={modalTitle}
        description={modalMode === 'create' ? 'El rol y responsable se asignan según tu posición jerárquica.' : 'Solo se habilitan los campos permitidos para tu rol.'}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal} disabled={saving}>Cancelar</Button>
            <Button type="submit" form="user-form" loading={saving}>Guardar usuario</Button>
          </>
        }
      >
        {formError && <Alert variant="danger" title="No se pudo guardar" className="mb-4">{formError}</Alert>}
        <form id="user-form" onSubmit={handleSubmit(submitUser)} className="space-y-4" noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input id="user-first-names" label="Nombres" required error={errors.nombres?.message} {...register('nombres', { required: 'Los nombres son obligatorios.' })} />
            <Input id="user-last-names" label="Apellidos" required error={errors.apellidos?.message} {...register('apellidos', { required: 'Los apellidos son obligatorios.' })} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input id="user-document" label="Cédula" inputMode="numeric" required error={errors.cedula?.message} {...register('cedula', { required: 'La cédula es obligatoria.', pattern: { value: /^[\d.\s-]+$/, message: 'Utiliza solamente números, puntos, espacios o guiones.' } })} />
            <Input id="user-phone" label="Número de celular" type="tel" required error={errors.celular?.message} {...register('celular', { required: 'El celular es obligatorio.' })} />
          </div>
          <Input id="user-email" type="email" label="Correo (opcional)" error={errors.email?.message} {...register('email', { pattern: { value: /^$|^\S+@\S+\.\S+$/, message: 'Ingresa un correo válido.' } })} />
          <Select id="user-status" label="Estado" options={[{ value: 'active', label: 'Activo' }, { value: 'inactive', label: 'Inactivo' }]} {...register('status')} />

          {modalMode === 'create' && user.role === ROLES.ADMINISTRADOR ? (
            <>
              <Select
                id="user-role"
                label="Tipo de usuario"
                placeholder="Seleccionar rol"
                required
                options={adminCreatableRoleOptions}
                error={errors.role?.message}
                {...register('role', { required: 'Selecciona el tipo de usuario.' })}
              />
              {[ROLES.COORDINADOR, ROLES.PUNTERO].includes(selectedRole) && (
                <Select
                  id="user-superior"
                  label={selectedRole === ROLES.COORDINADOR
                    ? 'Administrador general responsable'
                    : 'Coordinador responsable'}
                  placeholder="Seleccionar responsable"
                  required
                  options={supervisorOptions}
                  error={errors.superiorId?.message}
                  {...register('superiorId', {
                    required: 'Selecciona un responsable superior.',
                  })}
                />
              )}
            </>
          ) : modalMode === 'create' ? (
            <Input id="assigned-role" label="Rol asignado" value={ROLE_LABELS[creatableRole[user.role]]} disabled readOnly hint="El rol se determina por la jerarquía del usuario creador." />
          ) : null}

          {[ROLES.COORDINADOR, ROLES.PUNTERO].includes(selectedRole) && (
            <Input
              id="user-sectional"
              label="Seccional"
              required
              error={errors.seccional?.message}
              {...register('seccional', {
                required: 'La seccional es obligatoria para coordinadores y punteros.',
              })}
            />
          )}

          {modalMode === 'edit' ? (
            <>
              <Input id="current-role" label="Rol" value={ROLE_LABELS[editingUser?.role]} disabled readOnly />
              <Input id="current-supervisor" label="Responsable superior" value={editingUser?.superiorName || 'Sin responsable'} disabled readOnly hint="Las reasignaciones jerárquicas se realizan mediante acciones controladas." />
            </>
          ) : user.role !== ROLES.ADMINISTRADOR ? (
            <Input id="automatic-supervisor" label="Responsable superior" value={user.name} disabled readOnly hint="Se asignará automáticamente." />
          ) : null}
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        onConfirm={executeStatusChange}
        title={confirmAction?.type === 'activate' ? 'Activar usuario' : 'Desactivar usuario'}
        description={`¿Deseas ${confirmAction?.type === 'activate' ? 'activar' : 'desactivar'} a ${confirmAction?.user?.name || 'este usuario'}?`}
        confirmLabel={confirmAction?.type === 'activate' ? 'Sí, activar' : 'Sí, desactivar'}
        danger={confirmAction?.type !== 'activate'}
      />
      <InitialCredentialsModal
        credentials={credentials}
        onClose={() => setCredentials(null)}
      />
    </div>
  );
}

export default UsersPage;
